import React, { useState } from 'react';
import { productsAPI, uploadAPI } from '../../utils/api';
import { toast } from 'sonner';
import { Plus, Edit, Trash2 } from 'lucide-react';

const ProductManagement = ({ products, onRefresh }) => {
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [productImages, setProductImages] = useState([]);
  const [productForm, setProductForm] = useState({
    name: '', description: '', category: '', price: 0, discount_price: 0, sku: '', colors: '', stock: 0, is_featured: false, is_bestseller: false,
  });

  const resetForm = () => {
    setShowProductForm(false);
    setEditingProduct(null);
    setProductForm({ name: '', description: '', category: '', price: 0, discount_price: 0, sku: '', colors: '', stock: 0, is_featured: false, is_bestseller: false });
    setProductImages([]);
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploadingImage(true);
    try {
      const results = await Promise.all(Array.from(files).map(file => uploadAPI.uploadFile(file)));
      setProductImages(prev => [...prev, ...results.map(res => res.data.path)]);
      toast.success(`${files.length} image(s) uploaded`);
    } catch (_) { toast.error('Failed to upload images'); }
    finally { setUploadingImage(false); }
  };

  const handleSubmitProduct = async (e) => {
    e.preventDefault();
    try {
      const productData = { ...productForm, colors: productForm.colors.split(',').map(c => c.trim()).filter(c => c), images: productImages };
      if (editingProduct) {
        await productsAPI.update(editingProduct.id, productData);
        toast.success('Product updated');
      } else {
        await productsAPI.create(productData);
        toast.success('Product created');
      }
      resetForm();
      onRefresh();
    } catch (_) { toast.error('Failed to save product'); }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name, description: product.description, category: product.category,
      price: product.price, discount_price: product.discount_price || 0, sku: product.sku,
      colors: product.colors.join(', '), stock: product.stock, is_featured: product.is_featured, is_bestseller: product.is_bestseller,
    });
    setProductImages(product.images || []);
    setShowProductForm(true);
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await productsAPI.delete(id);
      toast.success('Product deleted');
      onRefresh();
    } catch (_) { toast.error('Failed to delete product'); }
  };

  return (
    <div data-testid="products-tab">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-cormorant text-2xl">Products Management</h2>
        <button onClick={() => { resetForm(); setShowProductForm(true); }} className="bg-[#C29B57] text-white px-6 py-2 rounded-md flex items-center gap-2 hover:bg-[#99733D] transition-colors" data-testid="add-product-button">
          <Plus className="w-4 h-4" /> Add Product
        </button>
      </div>

      {showProductForm && (
        <div className="bg-white p-6 rounded-lg shadow-sm mb-8" data-testid="product-form">
          <h3 className="font-cormorant text-xl mb-4">{editingProduct ? 'Edit Product' : 'Add New Product'}</h3>
          <form onSubmit={handleSubmitProduct} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input type="text" placeholder="Product Name" value={productForm.name} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} required className="border border-[#EBE4D5] rounded-md px-4 py-2" data-testid="product-name-input" />
              <input type="text" placeholder="SKU" value={productForm.sku} onChange={(e) => setProductForm({ ...productForm, sku: e.target.value })} required className="border border-[#EBE4D5] rounded-md px-4 py-2" data-testid="product-sku-input" />
            </div>
            <textarea placeholder="Description" value={productForm.description} onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} required rows="3" className="w-full border border-[#EBE4D5] rounded-md px-4 py-2" data-testid="product-description-input" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <select value={productForm.category} onChange={(e) => setProductForm({ ...productForm, category: e.target.value })} required className="border border-[#EBE4D5] rounded-md px-4 py-2" data-testid="product-category-select">
                <option value="">Select Category</option>
                <option value="Saree">Saree</option>
                <option value="Lehenga">Lehenga</option>
                <option value="Pakistani Suit">Pakistani Suit</option>
                <option value="Indo-Western">Indo-Western</option>
                <option value="Family Outfit">Family Outfit</option>
              </select>
              <input type="number" placeholder="Price" value={productForm.price} onChange={(e) => setProductForm({ ...productForm, price: parseFloat(e.target.value) })} required className="border border-[#EBE4D5] rounded-md px-4 py-2" data-testid="product-price-input" />
              <input type="number" placeholder="Discount Price" value={productForm.discount_price} onChange={(e) => setProductForm({ ...productForm, discount_price: parseFloat(e.target.value) || 0 })} className="border border-[#EBE4D5] rounded-md px-4 py-2" data-testid="product-discount-input" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input type="text" placeholder="Colors (comma separated)" value={productForm.colors} onChange={(e) => setProductForm({ ...productForm, colors: e.target.value })} className="border border-[#EBE4D5] rounded-md px-4 py-2" data-testid="product-colors-input" />
              <input type="number" placeholder="Stock" value={productForm.stock} onChange={(e) => setProductForm({ ...productForm, stock: parseInt(e.target.value) })} required className="border border-[#EBE4D5] rounded-md px-4 py-2" data-testid="product-stock-input" />
            </div>
            <div className="flex gap-4">
              <label className="flex items-center gap-2"><input type="checkbox" checked={productForm.is_featured} onChange={(e) => setProductForm({ ...productForm, is_featured: e.target.checked })} data-testid="product-featured-checkbox" /><span className="font-outfit text-sm">Featured</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={productForm.is_bestseller} onChange={(e) => setProductForm({ ...productForm, is_bestseller: e.target.checked })} data-testid="product-bestseller-checkbox" /><span className="font-outfit text-sm">Bestseller</span></label>
            </div>
            <div>
              <label className="block font-outfit text-sm mb-2">Product Images (Select multiple)</label>
              <input type="file" accept="image/*" multiple onChange={handleImageUpload} disabled={uploadingImage} className="mb-2" data-testid="image-upload-input" />
              {uploadingImage && <p className="text-sm text-[#C29B57]">Uploading images...</p>}
              {productImages.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {productImages.map((img, i) => (
                    <div key={img} className="relative w-20 h-20 bg-gray-100 rounded overflow-hidden">
                      <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${img}`} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => setProductImages(prev => prev.filter((_, idx) => idx !== i))} className="absolute top-0 right-0 bg-red-500 text-white text-xs px-1">x</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-4">
              <button type="submit" className="bg-[#C29B57] text-white px-6 py-2 rounded-md hover:bg-[#99733D] transition-colors" data-testid="save-product-button">Save Product</button>
              <button type="button" onClick={resetForm} className="bg-gray-200 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-300 transition-colors" data-testid="cancel-button">Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <table className="w-full" data-testid="products-table">
          <thead className="bg-[#F3EFE6]">
            <tr>
              <th className="px-6 py-3 text-left font-outfit text-sm uppercase tracking-wider">Product</th>
              <th className="px-6 py-3 text-left font-outfit text-sm uppercase tracking-wider">Category</th>
              <th className="px-6 py-3 text-left font-outfit text-sm uppercase tracking-wider">Price</th>
              <th className="px-6 py-3 text-left font-outfit text-sm uppercase tracking-wider">Stock</th>
              <th className="px-6 py-3 text-left font-outfit text-sm uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product) => (
              <tr key={product.id} className="border-t border-[#EBE4D5]" data-testid={`product-row-${product.id}`}>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#F3EFE6] rounded overflow-hidden">
                      {product.images?.length > 0 && <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${product.images[0]}`} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <span className="font-outfit text-sm">{product.name}</span>
                  </div>
                </td>
                <td className="px-6 py-4 font-outfit text-sm">{product.category}</td>
                <td className="px-6 py-4 font-outfit text-sm">₹{product.price}</td>
                <td className="px-6 py-4 font-outfit text-sm">{product.stock}</td>
                <td className="px-6 py-4">
                  <div className="flex gap-2">
                    <button onClick={() => handleEditProduct(product)} className="text-[#C29B57] hover:text-[#99733D]" data-testid={`edit-product-${product.id}`}><Edit className="w-4 h-4" /></button>
                    <button onClick={() => handleDeleteProduct(product.id)} className="text-[#9E2A2B] hover:text-[#7A1F1F]" data-testid={`delete-product-${product.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProductManagement;
